import React, { useEffect, useMemo } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Col,
  Flex,
  Form,
  Image,
  Modal,
  Row,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import {
  dateObjToDispViewDate,
  downloadUrl,
  formulateAttachmentPreview,
  readImageFileAsync,
  timeObjToDispViewTimeWithSuffix,
} from "../../../../../../utils/utils";
import RequiredLabel from "../../../../../common/required-label/RequiredLabel";
import { colors } from "../../../../../../constants/colors";
import { GetCardLayout } from "../../../../../common/getCardLayout";
import { routes } from "../../../../../../constants";
import {
  baseRouteMaker,
  baseRouteMakerForSuperAdmin,
} from "../../../../../../constants/routeConsts";
import { ArrowLeftOutlined } from "@ant-design/icons";
import styles from "./Appointments.module.scss";
import { getAppointmentsByIdAction } from "../../../../../../redux/services/appointments/appointmentsServices";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import Title from "antd/es/typography/Title";
import Spinner from "../../../../../common/spinner/Spinner";
import { StatusTag } from "../../../../../common/status-tag";
import PackageServiceAppointmentDialog from "./PackageServiceAppointmentDialog";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.guess();


export const getColorBasedOnStatus = (status) => {
  switch (status) {
    case "COMPLETED":
      return { background: colors.lightTeal, color: colors.darkTeal };

    case "PURCHASED":
      return {
        background: colors.lightPastelBlue,
        color: colors.electricBlue,
      };

    case "SCHEDULED":
      return {
        background: colors.darkGoldenRod,
        color: colors.mango,
      };

    case "REJECTED":
      return {
        background: colors.lightPinkishRed,
        color: colors.darkMaroon,
      };
    case "CANCELLED":
      return {
        background: colors.lightPinkishRed,
        color: colors.darkMaroon,
      };
    case "EXPIRED":
      return {
        background: colors.lightPinkishRed,
        color: colors.darkMaroon,
      };

    case "APPROVED":
      return { background: colors.slatePurple, color: colors.indigo };

    case "REFUNDED":
      return { background: "#ebe9e6", color: "#696969" };

    default:
      break;
  }
};


const AppointmentsDetails = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* dialog dependencies */
  const [openPackageServiceAppointmentDetailsDialog, setOpenPackageServiceAppointmentDetailsDialog] = useState(false);
  const [packageServiceAppointmentDetails, setPackageServiceAppointmentDetails] = useState(null);
  /* dialog dependencies */

  /* form dependencies */
  const [form] = Form.useForm();
  /* form dependencies */

  /* id dependencies */
  const { selectedAppointmentsId, selectedSuperAdminServiceIdForPackage } =
    useSelector((state) => state.appointments);
  /* id dependencies */

  /* detail dependencies */
  const [appointmentsInfoDetails, setAppointmentsInfoDetails] = useState(null);
  const [tempAppointmentsInfoDetails, setTempAppointmentsInfoDetails] =
    useState(null);
  /* detail dependencies */

  /* module dependencies for attachment */
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [attachmentList, setAttachmentList] = useState([]);
  const [servicesAttachmentList, setServicesAttachmentList] = useState([]);
  const [photoProofIdList, setPhotoProofIdList] = useState([]);
  /* module dependencies for attachment */

  /* table dependencies */
  const { systemSettings } = useSelector((state) => state.addonAndSponsorship);
  const [columns, setColumns] = useState([]);
  /* table dependencies */

  useEffect(() => {
    /* initial dependencies - Edit */
    if (
      selectedAppointmentsId &&
      selectedAppointmentsId !== "" &&
      selectedAppointmentsId !== null &&
      selectedAppointmentsId !== undefined
    ) {
      const dataToBeSent = {
        selectedAppointmentsId,
      };
      dispatch(getAppointmentsByIdAction(dataToBeSent, setLoading)).then(
        (apiRes) => {
          const response = apiRes;
          setAppointmentsInfoDetails(response);

          let tempAppointmentDetails = response;
          if (response?.order_type === "package") {
            const packageSingleService = response?.services?.find(
              (element) =>
                element?.service_id._id ===
                selectedSuperAdminServiceIdForPackage
            );
            tempAppointmentDetails = packageSingleService;
          }
          setTempAppointmentsInfoDetails(tempAppointmentDetails);

          setAttachmentList(
            response?.documents?.map((element) => ({
              ...element,
              url: element?.url,
            }))
          );
          setServicesAttachmentList(
            response?.service_id?.images?.map((element) => ({
              ...element,
              url: element?.url,
            }))
          );
        }
      );
    }
  }, []);

  /* open image preview for dialog - multiple attachment */
  const handleAttachmentPreview = async (file) => {
    const formulateAttachment = await formulateAttachmentPreview(
      file,
      file?.name,
      file.url
    );
    if (!formulateAttachment?.isFileTypeImage) {
      downloadUrl(formulateAttachment?.fileImagePreview);
    } else {
      if (!file.url && !file.preview) {
        file.preview = await readImageFileAsync(file.originFileObj);
      }
      setPreviewImageSrc(file?.url || file?.preview);
      setOpenPreviewDialog(true);
      const tempPreviewTitle =
        file?.name || file?.url?.substring(file?.url?.lastIndexOf("/") + 1);
      setPreviewTitle(tempPreviewTitle);
    }
  };
  /* open image preview for dialog - multiple attachment */

  
  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("servicesList.title"),
        dataIndex: "service_name",
        key: "service_name",
      },
      {
        title: t("servicesList.category"),
        dataIndex: "category",
        render: (_, { service_id }) => <>{service_id?.categoryInfo?.category?.name}</>,
      },
      {
        title: t("servicesList.price"),
        dataIndex: "service_price",
        key: "service_price",
        render: (_, { service_price }) => (
          <>{`${service_price} ${systemSettings?.system_currency}`}</>
        ),
      },
      {
        title: t("appointmentInfo.status"),
        key: "status",
        dataIndex: "status",
        render: (_, { status }) => {
          
          return (
            <>
              <Tag
                bordered={false}
                style={getColorBasedOnStatus(status)}
              >
                {status}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("view"),
        key: "view",
        dataIndex: "view",
        align: "center",
        render: (_, record) => {
          return (
            <>
              {record?.status !== "PURCHASED" &&
                <Typography.Link
                  onClick={(e) => {
                    setOpenPackageServiceAppointmentDetailsDialog(true);
                    setPackageServiceAppointmentDetails(record)
                  }}
                >
                  {t("view")}
                </Typography.Link>
              }
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const packageServiceAppointmentDetailsProps = useMemo(()=>({
    open:openPackageServiceAppointmentDetailsDialog,
    onClose:() => {
      setOpenPackageServiceAppointmentDetailsDialog(false);
      setPackageServiceAppointmentDetails(null)
    },
    appointmentsInfoDetails:packageServiceAppointmentDetails
  }),[openPackageServiceAppointmentDetailsDialog, packageServiceAppointmentDetails])

  return (
    <div className={styles.appointmentsInfoDetailsContainer}>
      <Flex vertical gap="2rem">
        <GetCardLayout>
          <Flex vertical gap="2rem">
            <Flex gap="1rem">
              <Button
                type="default"
                shape="circle"
                size="small"
                icon={<ArrowLeftOutlined />}
                onClick={() =>
                  navigate(
                    baseRouteMakerForSuperAdmin(routes.SALES_PAGE_APPOINTMENTS)
                  )
                }
              />
              <Typography.Title underline level={2}>
                {t("CommonUtils.customerInfo")}
              </Typography.Title>
            </Flex>
            {loading ? (
              <Spinner />
            ) : (
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.profileImage")}
                    required={false}
                  />
                  {appointmentsInfoDetails?.user_id?.profile_image ? (
                    <Image
                      style={{
                        width: "120px",
                        maxWidth: "100%",
                        border: "1px solid lightGray",
                        padding: "5px",
                        borderRadius: "10px",
                      }}
                      src={
                        appointmentsInfoDetails?.user_id?.profile_image ?? "-"
                      }
                      alt={t("CommonUtils.profileImageAlt")}
                    />
                  ) : (
                    <>-</>
                  )}
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.fullName")}
                    required={false}
                  />
                  <div>{`${
                    appointmentsInfoDetails?.user_id?.first_name ?? ""
                  } ${appointmentsInfoDetails?.user_id?.last_name ?? ""}`}</div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.email")}
                    required={false}
                  />
                  <div>{appointmentsInfoDetails?.user_id?.email ?? ""}</div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.phoneNumber")}
                    required={false}
                  />
                  <div>{`+${
                    appointmentsInfoDetails?.user_id?.phone?.country_code ?? ""
                  } ${
                    appointmentsInfoDetails?.user_id?.phone?.phone_number ?? ""
                  }`}</div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("ProfileSetup.countryLabel")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.user_id?.country?.name || "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.city")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.user_id?.address?.city?.name ||
                      "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.state")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.user_id?.address?.state?.name ||
                      "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.zipCode")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.user_id?.address?.zip_code || "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.gender")}
                    required={false}
                  />
                  <div>{appointmentsInfoDetails?.user_id?.gender || "-"}</div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.dateOfBirth")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.user_id?.date_of_birth
                      ? dateObjToDispViewDate(
                          appointmentsInfoDetails?.user_id?.date_of_birth,
                          "ddmmyyyy"
                        ) ?? "-"
                      : "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.weight")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.user_id?.weight
                      ? `${
                          appointmentsInfoDetails?.user_id?.weight?.number ??
                          "-"
                        } ${
                          appointmentsInfoDetails?.user_id?.weight?.unit ?? "-"
                        }`
                      : "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.height")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.user_id?.height
                      ? `${
                          appointmentsInfoDetails?.user_id?.height?.number ??
                          "-"
                        } ${
                          appointmentsInfoDetails?.user_id?.height?.unit ?? "-"
                        }`
                      : "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.accountStatus")}
                    required={false}
                  />
                  <StatusTag
                    status={
                      appointmentsInfoDetails?.user_id?.status === "active"
                        ? true
                        : false
                    }
                  />
                </Col>
                <Col span={24}>
                  <RequiredLabel
                    text={t("CommonUtils.photoIDProof")}
                    required={false}
                  />
                  {attachmentList?.length !== 0 &&
                  attachmentList[0]?.url !== "" ? (
                    <Flex vertical align="center" justify="center">
                      <Upload
                        listType="picture-card"
                        fileList={attachmentList}
                        accept="image/*"
                        onPreview={handleAttachmentPreview}
                        className="uploadContainer"
                      ></Upload>
                      <Modal
                        open={openPreviewDialog}
                        title={previewTitle}
                        footer={null}
                        onCancel={() => setOpenPreviewDialog(false)}
                      >
                        <img
                          style={{ width: "100%" }}
                          src={previewImageSrc}
                          alt={t("CommonUtils.photoProofAlt")}
                        />
                      </Modal>
                    </Flex>
                  ) : (
                    <>-</>
                  )}
                </Col>
              </Row>
            )}
          </Flex>
        </GetCardLayout>

        {/* Appointment Info */}
        {appointmentsInfoDetails?.order_type !== "package" && (
          <GetCardLayout>
            <Flex vertical gap="2rem">
              <Title underline level={2}>
                {t("CommonUtils.appointmentInfo")}
              </Title>
              <Row gutter={[16, 16]}>
                {appointmentsInfoDetails?.order_type === "package" && (
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.service")}
                      required={false}
                    />
                    <div>
                      {tempAppointmentsInfoDetails?.service_name
                        ? tempAppointmentsInfoDetails?.service_name
                        : "-"}
                    </div>
                  </Col>
                )}
                <Col span={8}>
                  <RequiredLabel
                    text={t("Modules.Appointments.appointmentTime")}
                    required={false}
                  />
                  <div>
                    {tempAppointmentsInfoDetails?.appointment_date_time
                      ? timeObjToDispViewTimeWithSuffix(
                          dayjs(
                            tempAppointmentsInfoDetails?.appointment_date_time
                          )
                            .utc()
                            .local()
                            .format("ddd, MMM D, H:mm z")
                        )
                      : "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("Modules.Appointments.appointmentDate")}
                    required={false}
                  />
                  <div>
                    {dateObjToDispViewDate(
                      tempAppointmentsInfoDetails?.appointment_date,
                      "ddmmyyyy"
                    )}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("Modules.Appointments.bookingFor")}
                    required={false}
                  />
                  <div>{tempAppointmentsInfoDetails?.booking_for}</div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("Modules.Appointments.charges")}
                    required={false}
                  />
                  <div>{appointmentsInfoDetails?.total_amount?.toFixed(2)}</div>
                </Col>

                <Col span={8}>
                  <RequiredLabel
                    text={t("Modules.Appointments.paymentStatus")}
                    required={false}
                  />
                  <Tag
                    bordered={false}
                    style={{
                      background: appointmentsInfoDetails?.is_payment_completed
                        ? colors.lightTeal
                        : colors.lightPinkishRed,
                      color: appointmentsInfoDetails?.is_payment_completed
                        ? colors.darkTeal
                        : colors.darkMaroon,
                    }}
                  >
                    {appointmentsInfoDetails?.is_payment_completed
                      ? t("Modules.Appointments.paid")
                      : t("Modules.Appointments.due")}
                  </Tag>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("Modules.Appointments.orderNumber")}
                    required={false}
                  />
                  <div>{appointmentsInfoDetails?.order_number}</div>
                </Col>

                {appointmentsInfoDetails?.booking_for === "other" && (
                  <Col span={24}>
                    <Flex vertical gap="1rem">
                      <Typography.Title level={3}>
                        {t("Modules.Appointments.bookingForDetails")}
                      </Typography.Title>
                      <Row gutter={[16, 16]}>
                        <Col span={8}>
                          <RequiredLabel
                            text={t("Modules.Appointments.name")}
                            required={false}
                          />
                          <div>{`${appointmentsInfoDetails?.booking_for_details?.first_name} ${appointmentsInfoDetails?.booking_for_details?.last_name}`}</div>
                        </Col>
                        <Col span={8}>
                          <RequiredLabel
                            text={t("Modules.Appointments.email")}
                            required={false}
                          />
                          <div>
                            {
                              appointmentsInfoDetails?.booking_for_details
                                ?.email
                            }
                          </div>
                        </Col>
                        <Col span={8}>
                          <RequiredLabel
                            text={t("Modules.Appointments.relationship")}
                            required={false}
                          />
                          <div>
                            {
                              appointmentsInfoDetails?.booking_for_details
                                ?.relationship
                            }
                          </div>
                        </Col>
                      </Row>
                    </Flex>
                  </Col>
                )}
              </Row>
            </Flex>
          </GetCardLayout>
        )}

        {appointmentsInfoDetails?.order_type === "package" && (
          <GetCardLayout>
            <Flex vertical gap="2rem">
              <Title underline level={2}>
                {t("CommonUtils.serviceInfo")}
              </Title>
              <Table
                dataSource={appointmentsInfoDetails?.services}
                columns={columns}
              />
            </Flex>
            <PackageServiceAppointmentDialog {...packageServiceAppointmentDetailsProps}/>
          </GetCardLayout>
        )}

        {/* Service Info */}
        {appointmentsInfoDetails?.service_id &&
          appointmentsInfoDetails?.service_id !== null &&
          appointmentsInfoDetails?.service_id !== undefined && (
            <GetCardLayout>
              <Flex vertical gap="2rem">
                <Title underline level={2}>
                  {t("CommonUtils.serviceInfo")}
                </Title>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <RequiredLabel
                      text={t("CommonUtils.documents")}
                      required={false}
                    />
                    {servicesAttachmentList?.length !== 0 ? (
                      <Flex vertical align="center" justify="center">
                        <Upload
                          listType="picture-card"
                          fileList={servicesAttachmentList}
                          accept="image/*"
                          onPreview={handleAttachmentPreview}
                          className="uploadContainer"
                        ></Upload>
                        <Modal
                          open={openPreviewDialog}
                          title={previewTitle}
                          footer={null}
                          onCancel={() => setOpenPreviewDialog(false)}
                        >
                          <img
                            style={{ width: "100%" }}
                            src={previewImageSrc}
                          />
                        </Modal>
                      </Flex>
                    ) : (
                      <>-</>
                    )}
                  </Col>

                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.service")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.service_id
                        ? appointmentsInfoDetails?.service_id?.title
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.category")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.service_id
                        ? appointmentsInfoDetails?.service_id?.categoryInfo
                            ?.category?.name
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.subCategory")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.service_id
                        ? appointmentsInfoDetails?.service_id?.categoryInfo
                            ?.subcategory?.name
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.availability")}
                      required={false}
                    />
                    <div>
                      <ol>
                        {appointmentsInfoDetails?.service_id &&
                          appointmentsInfoDetails?.service_id?.availability?.map(
                            (element) => {
                              return (
                                <li>{`${element.day} - ${element.startTime} to ${element.endTime}`}</li>
                              );
                            }
                          )}
                      </ol>
                    </div>
                  </Col>
                </Row>
              </Flex>
            </GetCardLayout>
          )}

        {appointmentsInfoDetails?.order_type !== "package" && appointmentsInfoDetails?.service_provider_id &&
          appointmentsInfoDetails?.service_provider_id !== null &&
          appointmentsInfoDetails?.service_provider_id !== undefined && (
            <GetCardLayout>
              <Flex vertical gap="2rem">
                <Typography.Title underline level={2}>
                  {t("CommonUtils.staffInfo")}
                </Typography.Title>
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.profileImage")}
                      required={false}
                    />
                    {appointmentsInfoDetails?.service_provider_id
                      ?.profile_image ? (
                      <Image
                        style={{
                          width: "120px",
                          maxWidth: "100%",
                          border: "1px solid lightGray",
                          padding: "5px",
                          borderRadius: "10px",
                        }}
                        src={
                          appointmentsInfoDetails?.service_provider_id
                            ?.profile_image ?? "-"
                        }
                        alt={t("CommonUtils.profileImageAlt")}
                      />
                    ) : (
                      <>-</>
                    )}
                  </Col>

                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.staffName")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.service_provider_id
                        ? `${appointmentsInfoDetails?.service_provider_id?.first_name} ${appointmentsInfoDetails?.service_provider_id?.last_name}`
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.email")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.service_provider_id
                        ? appointmentsInfoDetails?.service_provider_id?.email
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.phoneNumber")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.service_provider_id
                        ? `+${appointmentsInfoDetails?.service_provider_id?.phone?.country_code}-${appointmentsInfoDetails?.service_provider_id?.phone?.phone_number}`
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.fees")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.service_provider_id
                        ? appointmentsInfoDetails?.service_provider_id?.fees
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.experience")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.service_provider_id
                        ? `${
                            appointmentsInfoDetails?.service_provider_id
                              ?.experience
                          } ${t("CommonUtils.experienceUnit")}`
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.availability")}
                      required={false}
                    />
                    <div>
                      <ol>
                        {appointmentsInfoDetails?.service_provider_id &&
                          appointmentsInfoDetails?.service_provider_id?.availability?.map(
                            (element) => {
                              return (
                                <li>{`${element.day} - ${element.startTime} to ${element.endTime}`}</li>
                              );
                            }
                          )}
                      </ol>
                    </div>
                  </Col>
                </Row>
              </Flex>
            </GetCardLayout>
          )}
      </Flex>
    </div>
  );
};

export default AppointmentsDetails;
